import { type MutableRefObject, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { toLowerCamelCase } from 'utils/string';
import { CHANGE_BEHAVIOR_OPTION } from 'types/enums';
import type { AttributeType, FormItemDefinition } from 'types';
import type { InlineRecordType, UserAgendaTableDataSchemaType, ValidationResult } from 'types/userAgendaTable';
import type { TableRef } from 'components/common/Table/Table';
import { getChangeBehavior } from 'utils/userAgendaTableTypeGuard';

interface UseCellBlurHandlerProps {
  dataSchema: UserAgendaTableDataSchemaType;
  agendaId: string;
  isNestedList?: boolean;
  onValueChange?: (attributes?: InlineRecordType[]) => void;
  handleOnSaveEdit: () => Promise<void>;
  handleCalculateRow: (tableRef: MutableRefObject<TableRef | null>, agendaId: string, rowId: string, rowData: AttributeType[]) => Promise<void>;
  getRowDiff: (rowId: string) => AttributeType[] | undefined;
  getAllRowDiffs: () => InlineRecordType[];
  checkCalculateRequiredFields: (rowId: string, formData: Record<string, Record<string, unknown>>) => ValidationResult;
  highlightMissingFields: (rowId: string, missingFields: string[], t: (key: string) => string) => void;
  tableRef: MutableRefObject<TableRef | null>;
}

export const useCellBlurHandler = ({
  dataSchema,
  agendaId,
  isNestedList,
  onValueChange,
  handleOnSaveEdit,
  handleCalculateRow,
  getRowDiff,
  getAllRowDiffs,
  checkCalculateRequiredFields,
  highlightMissingFields,
  tableRef,
}: UseCellBlurHandlerProps) => {
  const { t } = useTranslation();

  return useCallback(
    async (e: React.FocusEvent<HTMLElement>) => {
      const target = e.target as HTMLInputElement | HTMLTextAreaElement;
      const name = target.name;

      if (!name || typeof name !== 'string' || !name.includes('.')) return;

      const [rowId, columnId] = name.split('.');
      const schemaDef = dataSchema[columnId];
      const formItemDef = schemaDef?.formItemDefinition;

      if (!formItemDef) return;

      const defKey = toLowerCamelCase(formItemDef.type) as keyof FormItemDefinition;
      const changeBehavior = getChangeBehavior(formItemDef, defKey);

      if (!changeBehavior || changeBehavior === CHANGE_BEHAVIOR_OPTION.NONE) return;

      if (changeBehavior === CHANGE_BEHAVIOR_OPTION.SAVE_REQUIRED) {
        await handleOnSaveEdit();
        return;
      }

      if (changeBehavior === CHANGE_BEHAVIOR_OPTION.CALCULATE) {
        const formData = tableRef.current?.getValues();
        if (!formData) return;

        const { allFilled, missingFields } = checkCalculateRequiredFields(rowId, formData as Record<string, Record<string, unknown>>);

        if (!allFilled) {
          highlightMissingFields(rowId, missingFields, t);
          toast.info(t('flow.modulesAndAgendas.form.item.records.calculate.fillAllRequiredFields'));
          return;
        }

        const diff = getRowDiff(rowId);
        if (!diff?.length) return;

        await handleCalculateRow(tableRef, agendaId, rowId, diff);

        if (isNestedList && onValueChange) {
          const inlineAttrs = getAllRowDiffs();
          onValueChange(inlineAttrs);
        }
      }
    },
    [
      dataSchema,
      agendaId,
      isNestedList,
      onValueChange,
      handleOnSaveEdit,
      handleCalculateRow,
      getRowDiff,
      getAllRowDiffs,
      checkCalculateRequiredFields,
      highlightMissingFields,
      tableRef,
      t,
    ]
  );
};
